import { Injectable, Inject } from '@angular/core';
import { AuthenticationDataService } from './authentication-data.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LoginRequest } from '../../models/authentication/login-request.model';
import { BaseNewUserRequest } from '../../models/base-user/base-new-user-request.model';
import { BaseUser } from '../../models/base-user/base-user.model';
import { ILeathermanAppConfig } from '../../config/app.config';
import { LeathermanAppConfigInjectionToken } from '../../config/injection-tokens/leatherman-app-config.injection-token';
import { Secure } from '../../enums/secure.enum';
import { AuthenticationDetails } from '../../models/authentication/authentication-details.model';

/**
 * A service to handle client authentication
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  // **********************
  // * Private properties
  // **********************

  /** Get accessor for the JWT token */
  private get token(): string {
    if (!this._token) {
      this._token = this.getTokenFromLocalStorage();
    }
    return this._token;
  }

  /** Set accessor for the JWT token */
  private set token(token: string) {
    this.setLocalStorage(token);
    this._token = token;
  }

  /** Reference to the JWT helper service */
  private jwtHelper = new JwtHelperService();
  private _token: string;

  /**
   * Constructor
   * @param config - The app config object
   * @param authenticationDataService - A reference to the authentication data service
   */
  constructor(
    @Inject(LeathermanAppConfigInjectionToken)
    private config: ILeathermanAppConfig,
    private authenticationDataService: AuthenticationDataService
  ) { }

  // **********************
  // * Public methods
  // **********************

  public async authenticate(loginRequest: LoginRequest): Promise<AuthenticationDetails> {
    const authenticationDetails = await this.authenticationDataService.authenticate(
      loginRequest
    );
    if (authenticationDetails.token && authenticationDetails.token.length > 0) {
      this.token = authenticationDetails.token;
    } else {
      this.clearToken();
    }
    return authenticationDetails;
  }

  /**
   * Clear the JWT token
   */
  public clearToken() {
    this._token = '';
    this.clearLocalStorage();
  }

  /**
   * Get the user's primary role
   * @returns The user's primary role
   * @async
   */
  public getPrimaryUserRole(): string {
    const userRoles = this.getUserRoles();
    if (userRoles.find(role => role === 'admin')) {
      return 'admin';
    }
    if (userRoles.find(role => role === 'user')) {
      return 'user';
    }
    return userRoles[0];
  }

  /**
   * Get the JWT token expiration date
   * @returns The date the token expires
   */
  public getTokenExpirationDate(): Date | null {
    const expirationDate = this.jwtHelper.getTokenExpirationDate(this.token);
    return expirationDate;
  }

  /**
   * Get the logged in user's ID
   * @returns The user's ID
   */
  public getUserId(): string {
    if (this.isAuthenticated() === false) {
      return null;
    }
    const tokenPayload = this.jwtHelper.decodeToken(this.token);
    return tokenPayload.userId;
  }

  /**
   * Get the user's roles
   * @returns An array of the user's roles
   */
  public getUserRoles(): string[] {
    if (this.isAuthenticated() === false) {
      return [];
    }
    const tokenPayload = this.jwtHelper.decodeToken(this.token);
    return tokenPayload.roles;
  }

  /**
   * Determine if the user is authenticated
   * @returns True if the user is authenticated
   */
  public isAuthenticated(): boolean {
    if (!this.token) {
      return false;
    }
    if (this.jwtHelper.isTokenExpired(this.token)) {
      this.clearLocalStorage();
      return false;
    }
    return true;
  }

  /**
   * Log in a user
   * @param loginRequest - The login request object
   * @returns True if the login succeeded
   * @async
   */
  public async loginUser<T extends LoginRequest>(loginRequest: T): Promise<AuthenticationDetails> {
    const authenticationDetails = await this.authenticationDataService.authenticate(loginRequest);
    if (authenticationDetails.token && authenticationDetails.token.length > 0) {
      this.token = authenticationDetails.token;
    } else {
      this.clearToken();
    }
    return authenticationDetails;
  }

  /**
   * Log out a user
   */
  public logoutUser() {
    this.clearToken();
  }

  /**
   * Register a new user
   * @param newUserRequest - The new user request
   * @param secure - An enum indicating whether the endpoing is secure
   */
  public async registerUser<T extends BaseNewUserRequest, U extends BaseUser>(
    newUserRequest: T,
    secure: Secure = Secure.false
  ): Promise<U> {
    const user = await this.authenticationDataService.register<T, U>(
      newUserRequest,
      secure
    );
    return user;
  }

  /**
   * Set the JWT token
   * @param token - The JWT token
   */
  public setToken(token: string) {
    this.setLocalStorage(token);
    this._token = token;
  }

  // **********************
  // * Private methods
  // **********************

  /**
   * Remove the JWT token from local storage
   */
  private clearLocalStorage() {
    localStorage.removeItem(this.config.jwtTokenName);
  }

  /**
   * Get the JWT token from local storage
   * @returns The JWT token
   */
  private getTokenFromLocalStorage(): string {
    const token = localStorage.getItem(this.config.jwtTokenName);
    if (!token) {
      return '';
    }
    return token;
  }

  /**
   * Save the JWT token to local storage
   * @param token - The JWT token
   */
  private setLocalStorage(token: string) {
    localStorage.setItem(this.config.jwtTokenName, token);
  }
}
